import { CalendarDaysIcon, ClockIcon, SunIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { FormRow, Select, TextInput } from '~/components';
import { SHIFT_DIFF_TYPE_OPTIONS } from '../../consts';
import { useNegotiationBaseline, useNegotiationInputsDisabled } from '../../provider';
import { TermCompareSection } from '../../sub-components/TermCompareSection';

export const ShiftDifferentialsSection = () => {
  const inputsDisabled = useNegotiationInputsDisabled();
  const negotiation = useNegotiationBaseline();
  const [weekdayShiftsChanged, setWeekdayShiftsChanged] = useState({
    eveShiftRateDiff: false,
    nightShiftRateDiff: false,
  });
  const [weekendShiftsChanged, setWeekendShiftsChanged] = useState({
    weDayShiftRateDiff: false,
    weEveShiftRateDiff: false,
    weNightShiftRateDiff: false,
  });
  const [holidayShiftsChanged, setHolidayShiftsChanged] = useState({
    holDayShiftRateDiff: false,
    holEveShiftRateDiff: false,
    holNightShiftRateDiff: false,
  });

  const [
    eveShiftRateDiff,
    nightShiftRateDiff,
    weDayShiftRateDiff,
    weEveShiftRateDiff,
    weNightShiftRateDiff,
    holDayShiftRateDiff,
    holEveShiftRateDiff,
    holNightShiftRateDiff,
  ] = useWatch({
    name: [
      'eveShiftRateDiff',
      'nightShiftRateDiff',
      'weDayShiftRateDiff',
      'weEveShiftRateDiff',
      'weNightShiftRateDiff',
      'holDayShiftRateDiff',
      'holEveShiftRateDiff',
      'holNightShiftRateDiff',
    ],
  });

  useEffect(() => {
    setWeekdayShiftsChanged({
      eveShiftRateDiff: eveShiftRateDiff !== negotiation?.eveShiftRateDiff,
      nightShiftRateDiff: nightShiftRateDiff !== negotiation?.nightShiftRateDiff,
    });
  }, [eveShiftRateDiff, nightShiftRateDiff, negotiation]);
  useEffect(() => {
    setWeekendShiftsChanged({
      weDayShiftRateDiff: weDayShiftRateDiff !== negotiation?.weDayShiftRateDiff,
      weEveShiftRateDiff: weEveShiftRateDiff !== negotiation?.weEveShiftRateDiff,
      weNightShiftRateDiff: weNightShiftRateDiff !== negotiation?.weNightShiftRateDiff,
    });
  }, [weDayShiftRateDiff, weEveShiftRateDiff, weNightShiftRateDiff, negotiation]);
  useEffect(() => {
    setHolidayShiftsChanged({
      holDayShiftRateDiff: holDayShiftRateDiff !== negotiation?.holDayShiftRateDiff,
      holEveShiftRateDiff: holEveShiftRateDiff !== negotiation?.holEveShiftRateDiff,
      holNightShiftRateDiff: holNightShiftRateDiff !== negotiation?.holNightShiftRateDiff,
    });
  }, [holDayShiftRateDiff, holEveShiftRateDiff, holNightShiftRateDiff, negotiation]);

  return (
    <div>
      <h3 className="sub-heading-sb text-left mb-5">Shift Differentials</h3>
      <div className="grid grid-cols-2 gap-4 items-center border border-grey-800 py-3 bg-white p-4 rounded-lg mb-4">
        <div className="flex items-center">
          <p className="mr-4 text-grey-400">Shift Differential Type:</p>
          <p className="font-bold text-grey-400">{negotiation?.shiftDiffType.value}</p>
        </div>
        <div className="flex items-center justify-end">
          <FormRow className="w-fit text-left">
            <Select
              name={'shiftDiffType'}
              options={SHIFT_DIFF_TYPE_OPTIONS}
              disabled={inputsDisabled}
            />
          </FormRow>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TermCompareSection
          changedTermsCount={Object.values(weekdayShiftsChanged).filter(Boolean).length}
          icon={<CalendarDaysIcon className="h-5 w-5" />}
          title="Weekday Shift Differentials"
          terms={[
            {
              label: 'Day',
              value: negotiation?.pricePerHour,
              prefix: '$',
              newValueField: <TextInput type="number" name={'pricePerHour'} disabled />,
            },
            {
              label: 'Evening',
              value: negotiation?.eveShiftRateDiff,
              newValueField: (
                <TextInput
                  type="number"
                  name={'eveShiftRateDiff'}
                  flagged={weekdayShiftsChanged.eveShiftRateDiff}
                  disabled={inputsDisabled}
                />
              ),
            },
            {
              label: 'Night',
              value: negotiation?.nightShiftRateDiff,
              newValueField: (
                <TextInput
                  type="number"
                  name={'nightShiftRateDiff'}
                  flagged={weekdayShiftsChanged.nightShiftRateDiff}
                  disabled={inputsDisabled}
                />
              ),
            },
          ]}
        />
        <TermCompareSection
          changedTermsCount={Object.values(weekendShiftsChanged).filter(Boolean).length}
          icon={<SunIcon className="h-5 w-5" />}
          title="Weekend Shift Differentials"
          terms={[
            {
              label: 'Day',
              value: negotiation?.weDayShiftRateDiff,
              newValueField: (
                <TextInput
                  type="number"
                  name={'weDayShiftRateDiff'}
                  flagged={weekendShiftsChanged.weDayShiftRateDiff}
                  disabled={inputsDisabled}
                />
              ),
            },
            {
              label: 'Evening',
              value: negotiation?.weEveShiftRateDiff,
              newValueField: (
                <TextInput
                  type="number"
                  name={'weEveShiftRateDiff'}
                  flagged={weekendShiftsChanged.weEveShiftRateDiff}
                  disabled={inputsDisabled}
                />
              ),
            },
            {
              label: 'Night',
              value: negotiation?.weNightShiftRateDiff,
              newValueField: (
                <TextInput
                  type="number"
                  name={'weNightShiftRateDiff'}
                  flagged={weekendShiftsChanged.weNightShiftRateDiff}
                  disabled={inputsDisabled}
                />
              ),
            },
          ]}
        />
        <TermCompareSection
          changedTermsCount={Object.values(holidayShiftsChanged).filter(Boolean).length}
          icon={<ClockIcon className="h-5 w-5" />}
          title="Holiday Shift Differentials"
          terms={[
            {
              label: 'Day',
              value: negotiation?.holDayShiftRateDiff,
              newValueField: (
                <TextInput
                  type="number"
                  name={'holDayShiftRateDiff'}
                  flagged={holidayShiftsChanged.holDayShiftRateDiff}
                  disabled={inputsDisabled}
                />
              ),
            },
            {
              label: 'Evening',
              value: negotiation?.holEveShiftRateDiff,
              newValueField: (
                <TextInput
                  type="number"
                  name={'holEveShiftRateDiff'}
                  flagged={holidayShiftsChanged.holEveShiftRateDiff}
                  disabled={inputsDisabled}
                />
              ),
            },
            {
              label: 'Night',
              value: negotiation?.holNightShiftRateDiff,
              newValueField: (
                <TextInput
                  type="number"
                  name={'holNightShiftRateDiff'}
                  flagged={holidayShiftsChanged.holNightShiftRateDiff}
                  disabled={inputsDisabled}
                />
              ),
            },
          ]}
        />
      </div>
    </div>
  );
};
